import { useRef, useState } from "react";
import type { StaffRequest } from "../api/clinicalWorkspace";

const dueLabel = (value: string) =>
  new Intl.DateTimeFormat("en-CA", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));

export function TodayStaffRequests({
  requests,
  loading,
  error,
  disabled,
  onRetry,
  onTransition,
  onOpen,
  onShowRoom,
  canShowRoom,
  onOpenReferralPdf,
  onOpenWorkItem,
}: {
  /** Opens the sealed referral PDF a request carries by reference (REF-5). */
  onOpenReferralPdf?: ((request: StaffRequest) => Promise<void>) | undefined;
  /** Opens the result or report a request is about, in Attention, where its holder works it. */
  onOpenWorkItem?: ((request: StaffRequest) => void) | undefined;
  onShowRoom?: (request: StaffRequest) => void;
  canShowRoom?: (request: StaffRequest) => boolean;
  requests: StaffRequest[];
  loading: boolean;
  error: boolean;
  disabled: boolean;
  onRetry: () => void;
  onTransition: (request: StaffRequest, targetStatus: "in_progress" | "completed") => Promise<void>;
  onOpen: (patient: { id: string; encounterId: string; displayName?: string }) => void;
}) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const lock = useRef(false);
  const openRequests = requests.filter(
    (request) => request.status === "open" || request.status === "in_progress",
  );
  const openReferralPdf = async (request: StaffRequest) => {
    if (onOpenReferralPdf === undefined) return;
    setMutationError(null);
    try {
      await onOpenReferralPdf(request);
    } catch (cause) {
      setMutationError(
        cause instanceof Error ? cause.message : "The referral PDF could not be opened.",
      );
    }
  };
  const transition = async (request: StaffRequest, targetStatus: "in_progress" | "completed") => {
    if (lock.current || disabled || error) return;
    lock.current = true;
    setPendingId(request.id);
    setMutationError(null);
    try {
      await onTransition(request, targetStatus);
    } catch (cause) {
      setMutationError(
        cause instanceof Error ? cause.message : "The staff request could not be updated.",
      );
    } finally {
      lock.current = false;
      setPendingId(null);
    }
  };
  return (
    <section className="tb-staff" aria-label="Needs staff">
      <header>
        <h2 className="tb-eyebrow">Needs staff</h2>
        <span className="tb-staff-scope">All clinic zones</span>
        <span>{error ? "Unavailable" : `${openRequests.length} open requests`}</span>
      </header>
      <p className="tb-muted tb-staff-explanation">
        Requests sent from rooms or encounters. Pick up a request, then mark it done after
        completing the work. Signing a note keeps unfinished requests here.
      </p>
      {error ? (
        <p className="tb-notice tb-notice-error" role="alert">
          Staff requests could not be refreshed.{" "}
          <button type="button" className="tb-button" onClick={onRetry}>
            Retry requests
          </button>
        </p>
      ) : null}
      {mutationError ? (
        <p className="tb-notice tb-notice-error" role="alert">
          {mutationError}
        </p>
      ) : null}
      {loading ? (
        <p className="tb-muted">Loading staff requests…</p>
      ) : openRequests.length ? (
        <ul>
          {openRequests.map((request) => (
            <li key={request.id} className="tb-staff-request">
              <span className="tb-attention-dot" aria-hidden="true" />
              <div className="tb-staff-request-content">
                <strong>
                  {request.displayName}
                  {request.roomName ? ` · ${request.roomName}` : ""}
                </strong>
                <p>{request.requestText}</p>
                {request.referralId ? (
                  <span className="tb-staff-attachment">
                    Referral package attached
                    {request.referralArtifactVersion === undefined
                      ? ""
                      : ` · version ${request.referralArtifactVersion}`}
                  </span>
                ) : null}
                <small>
                  Requested by {request.requestedBy} ·{" "}
                  {request.status === "open"
                    ? request.assignedToName
                      ? `For ${request.assignedToName}`
                      : "Waiting for staff"
                    : `Picked up${request.assignedToName ? ` by ${request.assignedToName}` : ""}`}
                  {request.dueAt ? ` · by ${dueLabel(request.dueAt)}` : ""}
                </small>
              </div>
              <div className="tb-room-actions">
                {onShowRoom && canShowRoom?.(request) ? (
                  <button
                    type="button"
                    className="tb-button"
                    onClick={() => onShowRoom(request)}
                    aria-label={`Show room for ${request.displayName}`}
                  >
                    Show room
                  </button>
                ) : null}
                {(request.resultId || request.reportId) && onOpenWorkItem ? (
                  <button
                    type="button"
                    className="tb-button"
                    onClick={() => onOpenWorkItem(request)}
                    aria-label={`Open ${request.resultId ? "result" : "report"} for ${request.displayName}`}
                  >
                    {request.resultId ? "Open result" : "Open report"}
                  </button>
                ) : null}
                {request.referralId && onOpenReferralPdf ? (
                  <button
                    type="button"
                    className="tb-button"
                    onClick={() => void openReferralPdf(request)}
                    aria-label={`Open referral PDF for ${request.displayName}`}
                  >
                    Open referral PDF
                  </button>
                ) : null}
                {request.encounterId ? (
                  <button
                    type="button"
                    className="tb-button"
                    onClick={() =>
                      onOpen({
                        id: request.patientId,
                        encounterId: request.encounterId as string,
                        displayName: request.displayName,
                      })
                    }
                    aria-label={`Encounter for ${request.displayName}`}
                  >
                    Encounter
                  </button>
                ) : null}
                <button
                  type="button"
                  className="tb-button"
                  disabled={disabled || error || pendingId !== null}
                  onClick={() =>
                    void transition(
                      request,
                      request.status === "open" ? "in_progress" : "completed",
                    )
                  }
                >
                  {pendingId === request.id
                    ? "Saving…"
                    : request.status === "open"
                      ? "Pick up"
                      : "Mark request done"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : !error ? (
        <p className="tb-muted">No outstanding staff requests.</p>
      ) : null}
    </section>
  );
}
